import { useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Input, Button } from '../../../components/ui';
import { useAuth } from '../../../context/AuthContext';
import { SignInFormProps, SignInData } from '../../../types';

const SignInForm: React.FC<SignInFormProps> = ({ onSuccess, onSwitchToSignUp, defaultEmail = '' }) => {
  const [formData, setFormData] = useState<SignInData>({
    email: defaultEmail,
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { signIn } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error } = await signIn({
        email: formData.email.trim(),
        password: formData.password
      });
      
      if (error) throw error;
      
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof SignInData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      <Input
        type="email"
        label="Email Address"
        value={formData.email}
        onChange={(e) => handleInputChange('email', e.target.value)}
        placeholder="Enter your email address"
        required
        fullWidth
      />

      <div>
        <Input
          type={showPassword ? 'text' : 'password'}
          label="Password"
          value={formData.password}
          onChange={(e) => handleInputChange('password', e.target.value)}
          placeholder="Enter your password"
          required
          fullWidth
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-gray-600"
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          }
        />
      </div>

      <Button
        type="submit"
        loading={loading}
        fullWidth
        size="lg"
      >
        Sign In
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={onSwitchToSignUp}
          className="text-sm text-emerald-600 hover:text-emerald-500 font-medium transition-colors"
        >
          Don't have an account? Create one
        </button>
      </div>
    </form>
  );
};

export default SignInForm; 